import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Lock, ArrowRight } from 'lucide-react'
import { Badge, Group, NavLink, Paper, Skeleton } from '@mantine/core'
import { notifications } from '@mantine/notifications'

import { listPrompts, type PromptSummaryDTO, type PromptInject } from '@/services/promptAdminService'
import { useAdminScope } from '../-admin/adminScope'
import { PromptEditor } from '../-prompts/PromptEditor'
import { PipelineGraph } from '../-prompts/PipelineGraph'
import '@/styles/pages.css'

export const Route = createFileRoute('/_app/organization/admin/prompts')({
  head: () => ({ meta: [{ title: 'Prompts · Admin · AERRIS' }] }),
  component: PromptsTab,
})

/** Display label for each Extraction Pipeline step. */
const STEP_LABEL: Record<string, string> = {
  EXTRACT: 'Extract',
  ENRICH: 'Enrich',
  RELATIONSHIPS: 'Relationships',
  MANAGEMENT: 'Management accounts',
}

function PromptsTab() {
  const { customerUuid, globalMode, clientId, canManage, accessLevel, scopeName, loading: scopeLoading } = useAdminScope()

  const [prompts, setPrompts] = useState<PromptSummaryDTO[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [promptsLoading, setPromptsLoading] = useState(true)

  const load = useCallback(async () => {
    if (scopeLoading) return
    setPromptsLoading(true)
    try {
      if (!canManage || (!globalMode && !clientId)) {
        setPrompts([])
        setSelected(null)
        return
      }
      // risk_policy is edited on the Risk tab (with the baseline it belongs beside) — hide it
      // here so there is exactly one editing surface for it.
      const list = (await listPrompts(customerUuid)).filter((p) => p.name !== 'risk_policy')
      // Group order follows the pipeline (stepOrder), then alphabetical within a step.
      const sorted = [...list].sort(
        (a, b) => (a.stepOrder ?? 99) - (b.stepOrder ?? 99) || a.name.localeCompare(b.name),
      )
      setPrompts(sorted)
      setSelected((prev) => (prev && sorted.some((p) => p.name === prev) ? prev : (sorted[0]?.name ?? null)))
    } catch (e) {
      console.error('Failed to load prompts:', e)
      notifications.show({ color: 'red', title: 'Could not load prompts', message: 'Please try again.' })
    } finally {
      setPromptsLoading(false)
    }
  }, [customerUuid, globalMode, clientId, canManage, scopeLoading])

  useEffect(() => {
    void load()
  }, [load])

  // Prompts grouped into ordered pipeline steps for the rail.
  const groups = useMemo(() => {
    const byStep = new Map<string, { order: number; prompts: PromptSummaryDTO[] }>()
    for (const p of prompts) {
      const step = p.step ?? 'OTHER'
      if (!byStep.has(step)) byStep.set(step, { order: p.stepOrder ?? 99, prompts: [] })
      byStep.get(step)!.prompts.push(p)
    }
    return [...byStep.entries()]
      .map(([step, v]) => ({ step, label: STEP_LABEL[step] ?? 'Other', ...v }))
      .sort((a, b) => a.order - b.order)
  }, [prompts])

  const selectedInjects = useMemo(
    () => prompts.find((p) => p.name === selected)?.injects ?? [],
    [prompts, selected],
  )

  const loading = scopeLoading || promptsLoading

  if (loading) {
    return (
      <Group align="flex-start" gap="lg" wrap="nowrap" aria-busy="true">
        <Skeleton height={320} width={280} radius="md" style={{ flexShrink: 0 }} />
        <Skeleton height={320} style={{ flex: 1 }} radius="md" />
      </Group>
    )
  }

  if (!canManage) {
    return (
      <div className="notice">
        <Lock size={16} />
        {globalMode
          ? 'Editing the global default prompts requires platform-admin access.'
          : !clientId
            ? 'Select a client to manage its prompts.'
            : `You need admin access to ${scopeName} to edit its prompts${accessLevel ? ` (your access: ${accessLevel})` : ''}.`}
      </div>
    )
  }

  if (prompts.length === 0) {
    return <div className="notice">No prompts found for {scopeName}.</div>
  }

  return (
    <>
      <div className="pipeline-graph-panel">
        <div className="pipeline-graph-label">Extraction pipeline</div>
        <PipelineGraph prompts={prompts} selected={selected} onSelect={setSelected} />
      </div>
      <Group align="flex-start" gap="lg" wrap="nowrap">
      <Paper withBorder radius="md" style={{ width: 260, flexShrink: 0, overflow: 'hidden' }}>
        {groups.map((g) => (
          <div key={g.step}>
            <div className="admin-versions-group-label">{g.label}</div>
            {g.prompts.map((p) => (
              <NavLink
                key={p.name}
                active={p.name === selected}
                onClick={() => setSelected(p.name)}
                label={<span title={p.name} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{p.name}</span>}
                rightSection={
                  <Badge size="xs" variant="light" color="gray">
                    {p.currentVersion != null ? `v${p.currentVersion}` : '—'}
                  </Badge>
                }
              />
            ))}
          </div>
        ))}
      </Paper>
      <div style={{ flex: 1, minWidth: 0 }}>
        {selectedInjects.length > 0 && <InjectionPanel injects={selectedInjects} />}
        {selected && (
          <PromptEditor
            key={`${customerUuid}:${selected}`}
            customerUuid={customerUuid}
            name={selected}
            canManage={canManage}
            onChanged={load}
          />
        )}
      </div>
      </Group>
    </>
  )
}

/** Field-level map of what this prompt injects from the Taxonomy. */
function InjectionPanel({ injects }: { injects: PromptInject[] }) {
  return (
    <div className="prompt-injects">
      <div className="prompt-injects-title">Injects from the taxonomy</div>
      {injects.map((inj) => (
        <div key={inj.placeholder} className="prompt-injects-row">
          <code className="prompt-injects-placeholder">{`{${inj.placeholder}}`}</code>
          <div className="prompt-injects-detail">
            <div className="prompt-injects-label">{inj.label}</div>
            <div className="prompt-injects-fields">
              {inj.fields.map((f) => (
                <span key={f} className="prompt-injects-field">{f}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
      <Link
        to="/organization/admin/taxonomy"
        search={(prev) => prev}
        className="prompt-injects-link"
      >
        View in Taxonomy <ArrowRight size={13} />
      </Link>
    </div>
  )
}
